import styled from '@emotion/styled';
import { Divider, Input, Table, Tag } from 'antd';
import { sortBy } from 'lodash';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import {
  TitreEntity,
  TitreOptionsType,
} from '../../../../entities/titre.entity';
import { PRIMARY, YELLOW } from '../../../../shared/colors';

const Container = styled.div`
  display: grid;
  grid-template-columns: minmax(180px, 0.72fr) minmax(0, 3.28fr);
  gap: 8px;
  color: var(--mt-text);
  height: 100%;
  box-sizing: border-box;
  padding: 8px;
  min-width: 0;
  min-height: 0;
  overflow: hidden;

  .ant-divider-horizontal {
    margin: 0px 0px 10px 0px;
  }

  th {
    background-color: ${PRIMARY} !important;
    color: white !important;
    font-weight: bold;
  }

  > .left-box {
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 8px;
    font-size: 12px;
    background: var(--mt-card);

    max-height: none;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 0.5vw;
    }

    &::-webkit-scrollbar-thumb {
      background-image: linear-gradient(to bottom, #eceff1, #eceff1);
      border-radius: 10px;
    }
  }

  > .right-box {
    min-width: 0;
    border: 1px solid var(--mt-border);
    border-radius: 8px;
    padding: 0;
    background: var(--mt-card);

    max-height: none;
    min-height: 0;
    overflow-x: auto;
    overflow-y: auto;

    &::-webkit-scrollbar {
      width: 0.5vw;
      height: 1vh;
    }

    &::-webkit-scrollbar-thumb {
      background-image: linear-gradient(to bottom, #eceff1, #eceff1);
      border-radius: 10px;
    }

    > .details {
      display: flex;

      > div {
        flex: 1;
        margin: 5px;
      }
    }
  }

  .board-header {
    align-items: center;
    background: var(--mt-card);
    border-bottom: 1px solid var(--mt-border);
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 9px 10px 8px;
  }

  .board-title {
    margin: 0;
  }

  .board-title span {
    color: var(--mt-muted);
    display: block;
    font-family: Poppins-R;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .board-title strong {
    color: var(--mt-text);
    display: block;
    font-family: Poppins-B;
    font-size: 16px;
    margin-top: 2px;
  }

  .board-count {
    border: 1px solid rgba(255, 176, 0, 0.34);
    border-radius: 999px;
    color: var(--mt-warning);
    font-family: Poppins-B;
    font-size: 12px;
    padding: 4px 9px;
    white-space: nowrap;
  }

  .filter-title {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
  }

  .filter-title strong {
    color: var(--mt-text);
    font-family: Poppins-B;
    font-size: 13px;
  }

  .filter-title span {
    color: var(--mt-muted);
    font-size: 11px;
  }

  .active-filters {
    padding: 8px 10px 0;
  }

  .board-table {
    background: var(--mt-card);
    padding: 8px 10px;
  }

  .elements {
    border: 1px solid var(--mt-border);
    padding: 4px 6px;
    max-height: 58px;
    border-radius: 6px;
    background-color: var(--mt-surface-strong);
    color: var(--mt-text);

    overflow-x: hidden;
    overflow-y: auto;
    margin-bottom: 3px;

    > p {
      margin: 0;
      cursor: pointer;
      padding-left: 2px;
      padding-top: 2px;
      padding-bottom: 2px;
      transition: background-color 0.18s ease, color 0.18s ease;

      &:hover {
        background-color: var(--mt-row-hover);
        border-radius: 5px;
      }
    }

    &::-webkit-scrollbar {
      width: 0.5vw;
    }

    &::-webkit-scrollbar-thumb {
      background-image: linear-gradient(to bottom, #eceff1, #eceff1);
      border-radius: 10px;
    }
  }

  .ant-table-column-sort {
    background-color: inherit;
  }

  .ant-table,
  .ant-table-container,
  .ant-table-content {
    background: transparent !important;
  }

  .ant-table-tbody > tr > td {
    background: inherit !important;
  }

  .ant-table-placeholder > td,
  .ant-empty-description {
    background: var(--mt-card) !important;
    color: var(--mt-muted) !important;
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;

    > .left-box,
    > .right-box {
      max-height: none;
    }

    > .left-box {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }

    .filter-group {
      min-width: 0;
    }
  }

  @media (max-width: 520px) {
    > .left-box {
      grid-template-columns: 1fr;
    }
  }
`;

export const SearchContent = ({
  mainScreen,
  titresData,
  titreOptions,
  getTitre,
}: {
  mainScreen: boolean;
  titresData: TitreEntity[];
  titreOptions?: TitreOptionsType;
  getTitre: (b: TitreEntity, ope: '1' | '2') => void;
}) => {
  const safeTitresData = useMemo(
    () => (Array.isArray(titresData) ? titresData : []),
    [titresData],
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [titres, setTitres] = useState<TitreEntity[]>(safeTitresData);
  const [titresFilter, setTitresFilter] =
    useState<TitreEntity[]>(safeTitresData);
  const [countries, setCountries] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [dateMaturation, setDateMaturation] = useState<string[]>([]);
  const [coupon, setCoupon] = useState<number[]>([]);
  const [filters, setFilters] = useState<FilterType[]>([]);

  useEffect(() => {
    setTitres(safeTitresData);
    setTitresFilter(safeTitresData);
  }, [safeTitresData]);

  useEffect(() => {
    setCountries(sortBy(titreOptions?.pays) || []);
    setRegions(sortBy(titreOptions?.region) || []);
    setDateMaturation(sortBy(titreOptions?.dateMaturite) || []);
    setCoupon(sortBy(titreOptions?.coupon) || []);
  }, [titreOptions]);

  useEffect(() => {
    if (filters) {
      console.log(filters);

      let resultFilter: TitreEntity[] = titres;
      for (let fil of filters) {
        if (fil.key === 'pays' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.pays),
          );
        } else if (fil.key === 'instrument' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.instrument),
          );
        } else if (fil.key === 'dateEmission' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.dateEmission),
          );
        } else if (fil.key === 'dateMaturite' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.dateMaturite),
          );
        } else if (fil.key === 'tauxMarginal' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(Number(res.tauxMarginal).toString()),
          );
        } else if (fil.key === 'code' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            res.code.includes(fil.values[0]),
          );
        } else if (fil.key === 'marche' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.marche),
          );
        } else if (fil.key === 'region' && fil.values.length !== 0) {
          resultFilter = resultFilter.filter((res) =>
            fil.values.includes(res.region),
          );
        }
      }
      setTitresFilter(resultFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const addFilter = (key: string, value: string) => {
    let dataFilters: FilterType[] = [];
    if (filters) {
      if (filters.find((fil) => fil.key === key) === undefined) {
        setFilters([...filters, { key, values: [value] }]);
      } else {
        for (let fil of filters) {
          if (fil.key === key && !fil.values.includes(value)) {
            dataFilters.push({ key: fil.key, values: [...fil.values, value] });
          } else {
            dataFilters.push(fil);
          }
        }
        setFilters(dataFilters);
      }
    } else {
      setFilters([{ key, values: [value] }]);
    }
  };

  const removeFilter = (key: string, value: string) => {
    let dataFilters: FilterType[] = [];
    if (filters) {
      for (let fil of filters) {
        if (fil.key === key && fil.values.includes(value)) {
          let index = fil.values.findIndex((val) => val === value);
          let temp = fil.values;
          temp.splice(index, 1);
          dataFilters.push({ key: fil.key, values: temp });
        } else {
          dataFilters.push(fil);
        }
      }
      setFilters(dataFilters);
    }
  };

  const lookupCode = (e: { target: { value: string } }) => {
    if (filters.length === 0) {
      addFilter('code', e.target.value);
    } else {
      const newFilters = filters.filter((e) => e.key !== 'code');
      newFilters.push({ key: 'code', values: [e.target.value] });
      setFilters(newFilters);
    }
  };

  return (
    <Container>
      <div className='left-box'>
        <div className='filter-title'>
          <strong>Filtres marché</strong>
          <span>{filters.reduce((total, item) => total + item.values.length, 0)} actifs</span>
        </div>
        <div className='filter-group'>
          <TitleLine>Chercher par Code</TitleLine>
          <div className='elements'>
            <Input
              placeholder='Code du Titre'
              size='small'
              onChange={lookupCode}
            />
          </div>
        </div>

        <div className='filter-group'>
          <TitleLine>Instrument</TitleLine>
          <div className='elements'>
            <p onClick={() => addFilter('instrument', 'OTA')}>OTA</p>
            <p onClick={() => addFilter('instrument', 'BTA')}>BTA</p>
            <p onClick={() => addFilter('instrument', 'Action')}>Action</p>
            <p onClick={() => addFilter('instrument', 'Emprunt oblig.')}>
              Emprunt oblig.
            </p>
          </div>
        </div>

        <div className='filter-group'>
          <TitleLine>Marche</TitleLine>
          <div className='elements'>
            <p onClick={() => addFilter('marche', 'BEAC')}>BEAC</p>
            <p onClick={() => addFilter('marche', 'BVMAC')}>BVMAC</p>
            <p onClick={() => addFilter('marche', 'BCEAO')}>BCEAO</p>
            <p onClick={() => addFilter('marche', 'BRVM')}>BRVM</p>
          </div>
        </div>

        <div className='filter-group'>
          <TitleLine>Region</TitleLine>
          <div className='elements'>
            {regions.map((val, index) => (
              <p key={index} onClick={() => addFilter('region', val)}>
                {val}{' '}
              </p>
            ))}
          </div>
        </div>

        <div className='filter-group'>
          <TitleLine>Pays</TitleLine>
          <div className='elements'>
            {countries.map((val, index) => (
              <p key={index} onClick={() => addFilter('pays', val)}>
                {val}{' '}
              </p>
            ))}
          </div>
        </div>

        <div className='filter-group'>
          <TitleLine>Date de maturation</TitleLine>
          <div className='elements'>
            {dateMaturation.map((val, index) => (
              <p key={index} onClick={() => addFilter('dateMaturite', val)}>
                {moment(val).format('DD/MM/YYYY')}
              </p>
            ))}
          </div>
        </div>

        <div className='filter-group'>
          <TitleLine>Coupon</TitleLine>
          <div className='elements'>
            {coupon.map((val, index) => (
              <p
                key={index}
                onClick={() => addFilter('tauxMarginal', val.toString())}
              >
                {val}%{' '}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className='right-box'>
        <div className='board-header'>
          <p className='board-title'>
            <span>Market board</span>
            <strong>Liste des titres disponibles</strong>
          </p>
          <div className='board-count'>{titresFilter.length} titres</div>
        </div>

        {filters && (
          <div className='active-filters'>
            {filters.map((filt, ind) => {
              return filt.values.map((val) => (
                <Tag
                  key={val}
                  color={YELLOW}
                  closable
                  onClose={() => removeFilter(filt.key, val)}
                >
                  {val}{' '}
                </Tag>
              ));
            })}
          </div>
        )}

        <div className='board-table'>
          <Table
            columns={COLUMNS}
            dataSource={titresFilter}
            rowSelection={{
              type: 'radio',
              onChange: (selectedRowKeys, selectedRows) => {
                getTitre(selectedRows[0], '1');
              },
            }}
            rowClassName={(row, index) =>
              index % 2 === 0 ? 'even-row' : 'odd-row'
            }
            rowKey='id'
            size='small'
            style={{ zoom: mainScreen ? 0.8 : 1 }}
            scroll={{ x: 780 }}
            pagination={false}
          />
        </div>
      </div>
    </Container>
  );
};

export const TitleLine = ({ children }: { children: string }) => {
  return (
    <div>
      <p style={{ fontFamily: 'Poppins-R', marginBottom: 0 }}>{children} </p>
      <Divider style={{ backgroundColor: 'var(--mt-border)' }} />
    </div>
  );
};

const COLUMNS = [
  {
    title: 'Pays',
    dataIndex: 'pays',
    sorter: (a: TitreEntity, b: TitreEntity) => a.pays.localeCompare(b.pays),
  },
  {
    title: 'Type',
    dataIndex: 'instrument',
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.instrument.localeCompare(b.instrument),
  },
  {
    title: 'CODE',
    dataIndex: 'code',
    sorter: (a: TitreEntity, b: TitreEntity) => a.code.localeCompare(b.code),
    render: (cell: any, row: TitreEntity) => (
      <div
        style={{
          width: 120,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        }}
      >
        {row.code}
      </div>
    ),
  },
  {
    title: 'Date de maturation',
    dataIndex: 'dateMaturite',
    render: (cell: any, row: TitreEntity) =>
      row.instrument === 'ACTION'
        ? '—'
        : `${moment(cell).format('DD/MM/YYYY')}`,
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.instrument === 'ACTION' || b.instrument === 'ACTION'
        ? 0
        : a.dateMaturite.localeCompare(b.dateMaturite),
  },
  {
    title: 'Durée',
    dataIndex: 'dureeMaturite',
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.instrument === 'ACTION' || b.instrument === 'ACTION'
        ? 0
        : a.dureeMaturite.localeCompare(b.dureeMaturite),
  },
  {
    title: 'Coupon',
    dataIndex: 'tauxMarginal',
    sorter: (a: TitreEntity, b: TitreEntity) =>
      a.instrument === 'ACTION' || b.instrument === 'ACTION'
        ? 0
        : a.tauxMarginal - b.tauxMarginal,
    render: (cell: any, row: TitreEntity) =>
      row.instrument === 'ACTION' ? '—' : <>{Number(row.tauxMarginal)}</>,
  },
];

type FilterType = {
  key: string;
  values: string[];
};
